import { atom } from 'jotai'

import type { ToolState } from '@/uiState'
import { hoverPreviewBudget, type HoverPreviewBudget } from '@/voxels/hoverPreviewBudget'

/**
 * Hover preview (large-volume plan S12, owner decision Q6). A runtime setting of the draw and erase
 * tools (`ScribbleSettings.hover`, `EraserSettings.hover`), which a page may override while it is open.
 *
 * - `voxels`: the hover preview is composed as real voxels: a one-sample `brush.stroke`, or a
 *   single-point `scribble.apply` where the brush does not sweep (cube/disc shapes, `onTop`).
 * - `ghost`: no preview events at all; a translucent sphere (`src/fiber/BrushGhost.tsx`) marks the
 *   brush volume instead, so the composed base is the durable base and nothing is remeshed on hover.
 * - `auto` (default): real voxels until they are measured to be slow on this machine for this
 *   material and size, then a ghost (`src/voxels/hoverPreviewBudget.ts`). Without a live renderer
 *   (tests) it falls back to the static rule: real voxels up to HOVER_GHOST_MIN_RADIUS.
 */
export type HoverPreviewMode = 'voxels' | 'ghost' | 'auto'
export type HoverPreviewPath = 'voxels' | 'ghost'

export const DEFAULT_HOVER_PREVIEW_MODE: HoverPreviewMode = 'auto'

/** Static fallback for `auto` when nothing is measured (no renderer): the largest real-voxel radius. */
export const HOVER_GHOST_MIN_RADIUS = 8

/**
 * Which hover preview a brush of `radius` gets under `mode`. Only the centred sphere brush has a
 * ghost (a cube or disc brush, or `onTop` placement, would not be what the sphere shows), so those
 * always preview as voxels. `key` names what is previewed for the measured budget (the placed type,
 * or `erase`); without one, or before the budget is live, `auto` uses the static radius rule.
 */
export function resolveHoverPreviewPath(
  mode: HoverPreviewMode,
  radius: number,
  shape: 'sphere' | 'cube' | 'disc' = 'sphere',
  placement: 'center' | 'onTop' = 'center',
  key?: string,
  budget: HoverPreviewBudget = hoverPreviewBudget,
): HoverPreviewPath {
  if (mode === 'voxels') return 'voxels'
  if (shape !== 'sphere' || placement !== 'center') return 'voxels'
  if (mode === 'ghost') return 'ghost'
  if (key === undefined || !budget.active) return radius > HOVER_GHOST_MIN_RADIUS ? 'ghost' : 'voxels'
  return budget.allowsVoxels(key, radius) ? 'voxels' : 'ghost'
}

/** The budget key of a hover: the eraser, or the type being placed. */
export function hoverBudgetKey(subtract: boolean, type: string | undefined): string {
  return subtract ? 'erase' : (type ?? 'solid')
}

/**
 * A page's own hover mode while it is open (null: the tools' own modes). Set by a page shell and
 * cleared on close, e.g. 007 Liquid metal uses `ghost` so hovering never re-meshes the metal surface.
 */
export const hoverPreviewModeOverrideAtom = atom<HoverPreviewMode | null>(null)

/** The hover mode of the draw tool (`subtract` false) or the eraser: the page override, else the tool's own setting. */
export function toolHoverPreviewMode(tool: ToolState, subtract: boolean, override: HoverPreviewMode | null): HoverPreviewMode {
  return override ?? (subtract ? tool.eraser?.hover : tool.scribble?.hover) ?? DEFAULT_HOVER_PREVIEW_MODE
}
